import { Alert, Box, Button, Group, Modal, Stack } from "@mantine/core";
import { IBim360FolderContent } from "@mctypes/global.types";
import { IMCBIMAPI } from "@mctypes/mcbim.types";
import { IconPlus } from "@tabler/icons-react";
import * as R from "ramda";
import React from "react";
import { useLocation } from "react-router-dom";
import { MCLoader } from "../../../../../components";
import BIM360AgFolderAndFileExplorer from "../../../../../components/Bim360AgFolderAndFileExplorer/BIM360AgFolderAndFileExplorer";
import { useAppDispatch, useAppSelector } from "../../../../../hooks";
import { doGetBim360ItemInfo } from "../../../../forge/forgeAPI";
import { useGetProjectQuery } from "../../../../projectRTK";
import { getProjectIdFromUrl } from "../../../../utils";
import { setSelectIfcFilesInRedux } from "../mcBimSlice";
import McBimAgFileTable from "./McBimAgFileTable";

const McBimBim360FileHandler: React.FC<{ mcBimProject: IMCBIMAPI.Project; actions?: React.ReactNode }> = ({
  mcBimProject,
  actions,
}) => {
  const selectedIfcFiles = useAppSelector((state) => state.mcBimReducer.selectedIfcFiles);
  const [filesModalOpen, setFilesModalOpen] = React.useState<boolean>(false);
  const [loadingExtraFileInfoFromBim360, setLoadingExtraFileInfoFromBim360] = React.useState<boolean>(false);
  const [failedToLoadExtraFileInfoFromBim360, setFailedToLoadExtraFileInfoFromBim360] = React.useState<boolean>(false);

  React.useEffect(() => {
    init();
  }, []);

  const dispatch = useAppDispatch();

  const init = () => {
    dispatch(setSelectIfcFilesInRedux(R.clone(mcBimProject.ifcFiles)));
  };

  const location = useLocation();
  const projectId = getProjectIdFromUrl(location.pathname);

  const { data: project } = useGetProjectQuery(projectId);

  const addFilesFromFileSelector = async (filesToAdd: IMCBIMAPI.IfcFile[]) => {
    try {
      setFilesModalOpen(false);
      setLoadingExtraFileInfoFromBim360(true);
      for (const ifcFile of filesToAdd) {
        const fileInfo = await doGetBim360ItemInfo(project._id, project.bim360?.projectId, ifcFile.fileStorageId);
        ifcFile.filePath = fileInfo?.attributes?.pathInProject;
      }
      dispatch(setSelectIfcFilesInRedux(filesToAdd));
      setLoadingExtraFileInfoFromBim360(false);
    } catch (error) {
      setLoadingExtraFileInfoFromBim360(false);
      setFailedToLoadExtraFileInfoFromBim360(true);
    }
  };

  return (
    <Stack h="100%" gap="xs" style={{ minHeight: 0 }}>
      {loadingExtraFileInfoFromBim360 === false && (
        <Group gap="xs" align="center">
          <Button
            color="mc-primary"
            variant="outline"
            leftSection={<IconPlus size={16} />}
            onClick={() => setFilesModalOpen(true)}
          >
            Legg til filer fra ACC
          </Button>
          {actions}
        </Group>
      )}
      {selectedIfcFiles?.length > 0 &&
        loadingExtraFileInfoFromBim360 !== true &&
        failedToLoadExtraFileInfoFromBim360 !== true && (
          <McBimAgFileTable
            ifcFiles={selectedIfcFiles}
            fileAttributeExtractSettings={mcBimProject.fileAttributeExtractSettings}
            onChangeIfcFiles={(updatedIfcFiles) => dispatch(setSelectIfcFilesInRedux(updatedIfcFiles))}
          />
        )}
      {loadingExtraFileInfoFromBim360 === true && <MCLoader loading text="Henter ytterligere filinformasjon ..." />}
      {failedToLoadExtraFileInfoFromBim360 === true && (
        <Alert color="red">
          Kunne ikke laste inn filinformasjon fra ACC. Prøv igjen senere. Hvis problemet vedvarer. Kontakt support
        </Alert>
      )}
      {filesModalOpen === true && (
        <FileSelectorModal
          onSelectIfcFiles={addFilesFromFileSelector}
          ifcFiles={selectedIfcFiles}
          toggle={() => setFilesModalOpen(false)}
        />
      )}
    </Stack>
  );
};

export default McBimBim360FileHandler;

const FileSelectorModal: React.FC<{
  toggle: () => void;
  ifcFiles: IMCBIMAPI.IfcFile[];
  onSelectIfcFiles: (ifcFiles: IMCBIMAPI.IfcFile[]) => void;
}> = ({ toggle, ifcFiles, onSelectIfcFiles }) => {
  const location = useLocation();
  const projectId = getProjectIdFromUrl(location.pathname);

  const { data: project } = useGetProjectQuery(projectId);

  const convertBim360FileContentToIfcFileAndSetIt = (files: IBim360FolderContent[]) => {
    const ifcFiles: IMCBIMAPI.IfcFile[] = files.map((file) => {
      return {
        fileName: file.name,
        fileStorageId: file.id,
        fileDescription: "",
        fileDiscipline: "",
        filePath: "",
      };
    });
    onSelectIfcFiles(ifcFiles);
  };

  return (
    <Modal opened onClose={toggle} title="Velg filer" size="95vw">
      <Box h={560} w="100%" style={{ overflow: "auto" }}>
        {project != null && (
            <BIM360AgFolderAndFileExplorer
              disableAbort={true}
              filterExtension={{
                label: "IFC",
                value: ".ifc",
              }}
              projectId={project._id}
              bim360ProjectId={project.bim360?.projectId}
              includeSelectedFilesHandler
              onSave={convertBim360FileContentToIfcFileAndSetIt}
              selectedAgFiles={ifcFiles?.map((file) => {
                return {
                  id: file.fileStorageId,
                  name: file.fileName,
                };
              })}
            />
        )}
      </Box>
    </Modal>
  );
};
